/**
 * 0.44.92 - The header's brand text: two lines, each per language.
 *
 * Stored in the header layout config as
 *   brand_lines: [ { labels: { <lang>: text }, authoring_only: bool }, ... ]   (at most 2)
 *   brand_authoring_language: 'hy'   (the language an authoring-only line is written in)
 *
 * 0.44.98 - Each line has its own font size, a Henaket size key
 * (size: 'sm' | 'base' | 'lg' | 'xl', default 'lg').
 * 0.44.99 - And its weight (weight: 'regular' | 'semibold' | 'bold',
 * default 'semibold') and underline (underline: true / false).
 * 0.46.79 - And how it shows on small screens (under 480px):
 *   mobile_mode 'wrap'      as on large screens (the default)
 *               'truncate'  one line, cut with "..."
 *               'alt'       a shorter text of its own, per language:
 *                           mobile_labels { <lang>: text } and
 *                           mobile_authoring_only, as the line's own
 *                           (an empty alternative hides the line there)
 *
 * A text may be a library value (=lib(...)), resolved per language by
 * the caller (useResolvedTitle). Before 0.44.92 there was one line for
 * every language, brand_text: it reads as line 1, the same in every
 * language, until the header is saved again.
 */
export const BRAND_LINE_COUNT = 2;

/* 0.44.98 - The Henaket sizes a brand line may use: the token, and its
 * largest pixel size (from the desktop breakpoint), for the line count. */
export const BRAND_SIZES = {
  sm: { token: '--font-size-sm', px: 14, label: 'Small (14 px)' },
  base: { token: '--font-size-base', px: 16, label: 'Normal (16 px)' },
  lg: { token: '--font-size-lg', px: 20, label: 'Large (18 / 20 px)' },
  xl: { token: '--font-size-xl', px: 24, label: 'Extra large (20 / 24 px)' },
};
export const BRAND_SIZE_DEFAULT = 'lg';
const sizeOf = (s) => (BRAND_SIZES[s] ? s : BRAND_SIZE_DEFAULT);

/* 0.44.99 - The Henaket weights a brand line may use. */
export const BRAND_WEIGHTS = {
  regular: { token: '--font-weight-regular', label: 'Not bold' },
  semibold: { token: '--font-weight-semibold', label: 'Semibold' },
  bold: { token: '--font-weight-bold', label: 'Bold' },
};
export const BRAND_WEIGHT_DEFAULT = 'semibold';

/* 0.46.79 - How a line shows on small screens. */
export const BRAND_MOBILE_MODES = [
  { value: 'wrap', label: 'Wrap, as on large screens' },
  { value: 'truncate', label: 'One line, cut with "..."' },
  { value: 'alt', label: 'Alternative text' },
];
const mobileModeOf = (m) => (BRAND_MOBILE_MODES.some(x => x.value === m) ? m : 'wrap');
const weightOf = (w) => (BRAND_WEIGHTS[w] ? w : BRAND_WEIGHT_DEFAULT);

export function brandLinesOf(cfg) {
  const c = cfg || {};
  const raw = Array.isArray(c.brand_lines) ? c.brand_lines : [];
  const lines = [];
  for (let i = 0; i < BRAND_LINE_COUNT; i += 1) {
    const l = raw[i] || {};
    lines.push({
      labels: (l.labels && typeof l.labels === 'object') ? { ...l.labels } : {},
      authoring_only: !!l.authoring_only,
      size: sizeOf(l.size),
      weight: weightOf(l.weight),
      underline: !!l.underline,
      mobile_mode: mobileModeOf(l.mobile_mode),
      mobile_labels: (l.mobile_labels && typeof l.mobile_labels === 'object') ? { ...l.mobile_labels } : {},
      mobile_authoring_only: !!l.mobile_authoring_only,
    });
  }
  const empty = lines.every(l => !Object.values(l.labels).some(v => String(v || '').trim()));
  const legacy = String(c.brand_text || '').trim();
  if (empty && legacy) {
    lines[0] = { labels: { [c.brand_authoring_language || '*']: legacy }, authoring_only: true,
                 size: BRAND_SIZE_DEFAULT, weight: BRAND_WEIGHT_DEFAULT, underline: false,
                 mobile_mode: 'wrap', mobile_labels: {}, mobile_authoring_only: false };
  }
  return lines;
}

/* The text of one line in a language (unresolved: may be =lib(...)). */
export function brandLineText(line, lang, authoringLang) {
  if (!line) return '';
  const labels = line.labels || {};
  const auth = labels[authoringLang] !== undefined ? authoringLang
    : (labels['*'] !== undefined ? '*' : authoringLang);
  if (line.authoring_only) {
    return String(labels[auth] || Object.values(labels).find(v => String(v || '').trim()) || '');
  }
  return String(labels[lang] || labels[auth] || '');
}

/* The non-empty lines in a language, for the header:
 * [{ raw, size, weight, underline }]. */
export function brandTexts(cfg, lang) {
  const auth = (cfg && cfg.brand_authoring_language) || '';
  return brandLinesOf(cfg)
    .map(l => ({ raw: brandLineText(l, lang, auth).trim(), size: l.size,
                 weight: l.weight, underline: l.underline,
                 // 0.46.79 - Small screens.
                 mobileMode: l.mobile_mode,
                 mobileRaw: l.mobile_mode === 'alt'
                   ? brandLineText({ labels: l.mobile_labels, authoring_only: l.mobile_authoring_only }, lang, auth).trim()
                   : '' }))
    .filter(l => l.raw);
}
